import { useState } from "react";
import { useJsApiLoader } from '@react-google-maps/api';
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import RegionPolygon from "./RegionPolygon";
import DepartmentMarker from "./DepartmentMarker";
import DepartmentInfoWindow from "./DepartmentInfoWindow";
import DepartmentList from "./DepartmentList";
import DepartmentsByRegion from "./DepartmentsByRegion";
import InterventionGoogleMap from "./InterventionGoogleMap";
import { departments, Department } from "./departmentData";
import { regionPolygons } from "./regionData";

// Google Maps API key
const googleMapsApiKey = "AIzaSyBkRcnHgBuwc4v-pz69p2UXml2skVY93CU";

const InterventionMap = () => {
  const [activeRegion, setActiveRegion] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({ 
    lat: 46.227638, lng: 2.213749 // Default center of France
  });
  const [mapZoom, setMapZoom] = useState(5);
  const [selectedDept, setSelectedDept] = useState<Department | null>(null);
  const { toast } = useToast();

  // Use the useJsApiLoader hook instead of LoadScript component
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleMapsApiKey
  });

  // Group departments by region for the UI
  const departmentsByRegion = departments.reduce((acc, dept) => {
    if (!acc[dept.region]) {
      acc[dept.region] = [];
    }
    acc[dept.region].push(dept);
    return acc;
  }, {} as Record<string, Department[]>);

  const handleRegionClick = (region: string) => {
    setActiveRegion(region === activeRegion ? null : region);
    
    // Fly to the selected region
    const regionCoordinates = {
      "Occitanie": { center: { lat: 43.7, lng: 2.0 }, zoom: 7 },
      "Auvergne-Rhône-Alpes": { center: { lat: 45.5, lng: 3.5 }, zoom: 7 },
      "Île-de-France": { center: { lat: 48.8, lng: 2.3 }, zoom: 8.5 }
    }[region];
    
    if (regionCoordinates) {
      setMapCenter(regionCoordinates.center);
      setMapZoom(regionCoordinates.zoom);
    }
  };

  const handleMarkerClick = (dept: Department) => {
    setSelectedDept(dept);
  };
  
  const handleCloseInfoWindow = () => {
    setSelectedDept(null);
  };

  return (
    <section className="py-16 bg-white" id="zones-intervention">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-8">
          <h2 className="text-3xl font-heading font-bold mb-2 text-fabre-gray">
            NOS ZONES D'INTERVENTION
          </h2>
          <div className="w-20 h-1 bg-fabre-green mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 mb-8">
            Le Groupe Fabre Environnement intervient principalement dans le Sud de la France et en Île-de-France
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div className="col-span-1 md:col-span-2">
            <div className="w-full h-[500px] rounded-lg shadow-lg border border-gray-200 overflow-hidden relative">
              <InterventionGoogleMap 
                isLoaded={isLoaded}
                mapCenter={mapCenter}
                mapZoom={mapZoom}
                toast={toast}
              >
                {/* Add region polygons */}
                {Object.entries(regionPolygons).map(([region, coords]) => (
                  <RegionPolygon 
                    key={region} 
                    region={region} 
                    coordinates={coords} 
                    onClick={handleRegionClick}
                  />
                ))}
                
                {/* Add markers for each department */}
                {departments.map(dept => (
                  <DepartmentMarker 
                    key={dept.code} 
                    department={dept} 
                    onClick={handleMarkerClick} 
                  />
                ))}
                
                {/* InfoWindow for selected department */}
                {selectedDept && (
                  <DepartmentInfoWindow 
                    department={selectedDept} 
                    onCloseClick={handleCloseInfoWindow} 
                  />
                )}
              </InterventionGoogleMap>
            </div>
          </div>
          
          <div className="col-span-1 space-y-6">
            <DepartmentList 
              departmentsByRegion={departmentsByRegion}
              activeRegion={activeRegion}
              onRegionClick={handleRegionClick}
            />

            <div className="text-center bg-fabre-lightgreen p-6 rounded-lg shadow">
              <h3 className="text-xl font-heading font-semibold mb-4 text-fabre-darkgreen">
                Besoin d'un service ?
              </h3>
              <p className="text-fabre-gray mb-4">
                Découvrez l'ensemble de nos sites et services disponibles près de chez vous.
              </p>
              <Button asChild className="bg-fabre-green hover:bg-fabre-darkgreen">
                <Link to="/sites" className="flex items-center">
                  <MapPin className="mr-2 h-5 w-5" /> Tous nos sites
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <DepartmentsByRegion departmentsByRegion={departmentsByRegion} />
      </div>
    </section>
  );
};

export default InterventionMap;
